import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Circle,
  Tag,
  Tooltip,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon, LockIcon } from "@chakra-ui/icons";
import PageHeader from "../components/PageHeader";
import { inviteSubject } from "../utils/inviteSubject";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getWcPool, updateWcPool, removeWcMember, toggleWcMemberPaid, toggleWcCoAdmin, sendWcInvites, getWcPayouts, saveWcPayouts, sendWcRecapTest } from "../api/worldCup";
import { generateInviteLink } from "../api/invite";
import PayoutTotalLine from "../components/PayoutTotalLine";
import { payoutTotalError } from "../utils/payoutTotals";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE    = "#00007f";
const WC_SEASON_ID = 2;

export default function WorldCupPoolSetup() {
  const { poolId } = useParams();
  const navigate   = useNavigate();
  const { user, logout } = useAuth();
  const toast = useToast();

  const username = user?.username || "Player";

  const [pool,        setPool]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState("");

  // Settings form state
  const [name,          setName]          = useState("");
  const [poolDepth,     setPoolDepth]     = useState("");
  const [maxUsers,      setMaxUsers]      = useState("");
  const [teamsPerUser,  setTeamsPerUser]  = useState("");
  const [isPrivate,     setIsPrivate]     = useState(false);
  const [swapEnabled,   setSwapEnabled]   = useState(true);
  const [draftDate,     setDraftDate]     = useState("");
  const [draftHour,     setDraftHour]     = useState(18);
  const [draftMinute,   setDraftMinute]   = useState(0);
  const [saving,        setSaving]        = useState(false);
  const [saveError,     setSaveError]     = useState("");
  const [weeklyRecap,   setWeeklyRecap]   = useState(true);
  const [sendingRecap,  setSendingRecap]  = useState(false);

  // Payout state
  const [payouts,       setPayouts]       = useState([]);
  const [payoutError,   setPayoutError]   = useState("");
  const [savingPayouts, setSavingPayouts] = useState(false);

  // Invite state
  const [emailInput,    setEmailInput]    = useState("");
  const [emails,        setEmails]        = useState([]);
  const [sendMode,      setSendMode]      = useState("invite"); // "invite" | "message"
  const [message,       setMessage]       = useState("");
  const [sending,       setSending]       = useState(false);
  const [inviteLink,    setInviteLink]    = useState("");
  const [linkLoading,   setLinkLoading]   = useState(false);
  const [invitesClosed, setInvitesClosed] = useState(false);

  useEffect(() => {
    loadPool();
  }, [poolId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPool() {
    setLoading(true);
    setLoadError("");
    try {
      const [data, payoutData] = await Promise.all([
        getWcPool(WC_SEASON_ID, poolId),
        getWcPayouts(WC_SEASON_ID, poolId),
      ]);
      setPool(data);
      setName(data.name || "");
      setPoolDepth(data.pool_depth != null ? String(data.pool_depth) : "");
      setMaxUsers(data.max_users != null ? String(data.max_users) : "");
      setTeamsPerUser(data.teams_per_user != null ? String(data.teams_per_user) : "");
      setIsPrivate(data.is_private ?? false);
      setSwapEnabled(data.swap_enabled ?? true);
      setWeeklyRecap(data.weekly_recap_enabled ?? true);
      if (data.draft_starts_at) {
        const dt = data.draft_starts_at.slice(0, 16); // "YYYY-MM-DDTHH:mm"
        setDraftDate(dt.slice(0, 10));
        setDraftHour(parseInt(dt.slice(11, 13), 10));
        const rawMin = parseInt(dt.slice(14, 16), 10);
        setDraftMinute(Math.round(rawMin / 5) * 5 % 60);
      } else {
        setDraftDate("");
        setDraftHour(18);
        setDraftMinute(0);
      }
      setPayouts(payoutData.length > 0 ? payoutData : [
        { label: "1st Place", amount: "" },
        { label: "2nd Place", amount: "" },
        { label: "3rd Place", amount: "" },
      ]);
      try {
        const { link } = await generateInviteLink({ game: "wc", poolId: Number(poolId), seasonId: WC_SEASON_ID });
        setInviteLink(link);
        setMessage(buildInviteMessage(data.name, username, link, data.pool_depth));
      } catch {
        setInvitesClosed(true);
        setSendMode("message");
      }
    } catch (err) {
      setLoadError(err?.response?.data?.detail || "Failed to load pool.");
    } finally {
      setLoading(false);
    }
  }

  function buildInviteMessage(poolName, inviterName, link, depth) {
    // Stated up front so an invitee knows the cost before joining.
    // Omitted entirely when the pool has no buy-in.
    const amount = Number(depth);
    const buyIn =
      depth && !Number.isNaN(amount) && amount > 0
        ? ` The buy-in for this pool is currently $${
            Number.isInteger(amount) ? amount : amount.toFixed(2)
          }.`
        : "";
    return (
      `Hi!\n\nYou've been invited to join "${poolName}" on The Deep End — a World Cup Draft Pool — by ${inviterName}.${buyIn}\n\n` +
      `Click the link below to join directly:\n${link}\n\n` +
      `Good luck!\n— ${inviterName}`
    );
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function addEmail() {
    // Split on commas, semicolons and whitespace so a pasted list becomes
    // separate tags. Typing fires this per address via the key handler, but a
    // paste never does, and the whole string would otherwise be accepted as
    // one address because it happens to contain an "@".
    const parts = emailInput
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));
    if (!parts.length) return;
    setEmails((prev) => {
      const next = [...prev];
      parts.forEach((e) => { if (!next.includes(e)) next.push(e); });
      return next;
    });
    setEmailInput("");
  }

  function handleEmailKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  }

  function removeEmail(addr) {
    setEmails((prev) => prev.filter((e) => e !== addr));
  }

  function addAllMembers() {
    const memberEmails = (pool?.members || [])
      .map((m) => m.email)
      .filter((e) => e && e.includes("@") && !emails.includes(e));
    if (memberEmails.length > 0) setEmails((prev) => [...prev, ...memberEmails]);
  }

  async function handleSave() {
    setSaveError("");
    const tpu  = parseInt(teamsPerUser, 10);
    const maxU = parseInt(maxUsers, 10);
    if (!name.trim()) { setSaveError("Pool name is required."); return; }
    if (!tpu || tpu < 1 || tpu > 10) { setSaveError("Countries per user must be 1–10."); return; }
    if (!maxU || maxU < 2 || maxU > 12) { setSaveError("Number of users must be 2–12."); return; }

    setSaving(true);
    try {
      const updated = await updateWcPool(WC_SEASON_ID, poolId, {
        name: name.trim(),
        teams_per_user: tpu,
        max_users: maxU,
        pool_depth: poolDepth ? parseInt(poolDepth, 10) : null,
        draft_starts_at: draftDate
          ? `${draftDate}T${String(draftHour).padStart(2, "0")}:${String(draftMinute).padStart(2, "0")}`
          : null,
        is_private: isPrivate,
        swap_enabled: swapEnabled,
      });
      setPool(updated);
      toast({ title: "Settings saved.", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePayouts() {
    setPayoutError("");
    const rows = payouts.map((p) => ({ label: p.label.trim(), amount: parseFloat(p.amount) }));
    if (rows.some((r) => !r.label || isNaN(r.amount) || r.amount <= 0)) {
      setPayoutError("All rows must have a label and a positive amount.");
      return;
    }
    // Allow under, block over — see utils/payoutTotals.js. The server
    // enforces the same rule; this is it said early.
    const overPot = payoutTotalError(rows.map((r) => r.amount), pool?.total_pot);
    if (overPot) {
      setPayoutError(overPot);
      return;
    }
    setSavingPayouts(true);
    try {
      const saved = await saveWcPayouts(WC_SEASON_ID, poolId, rows);
      setPayouts(saved);
      toast({ title: "Payouts saved.", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      setPayoutError(err?.response?.data?.detail || "Failed to save payouts.");
    } finally {
      setSavingPayouts(false);
    }
  }

  async function handleRemoveMember(userId) {
    // Irreversible and one stray click from the paid/co-admin checkboxes, so
    // confirm by name first.
    const member = (pool?.members || []).find((m) => m.user_id === userId);
    const who = member?.username || member?.email || "this member";
    if (!window.confirm(`Remove ${who} from this pool? Their picks will no longer count.`)) {
      return;
    }

    try {
      await removeWcMember(WC_SEASON_ID, poolId, userId);
      await loadPool();
      toast({ title: "Member removed.", status: "info", duration: 3000, isClosable: true });
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to remove member.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }

  async function handleTogglePaid(userId, newPaid) {
    try {
      await toggleWcMemberPaid(WC_SEASON_ID, poolId, userId, newPaid);
      setPool((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.user_id === userId ? { ...m, paid: newPaid } : m
        ),
      }));
    } catch (err) {
      toast({
        title: err?.response?.data?.detail || "Failed to update payment status.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }

  async function switchToInvite() {
    setSendMode("invite");
    if (inviteLink) {
      setMessage(buildInviteMessage(pool?.name || "", username, inviteLink, pool?.pool_depth));
      return;
    }
    setLinkLoading(true);
    try {
      const { link } = await generateInviteLink({ game: "wc", poolId: Number(poolId), seasonId: WC_SEASON_ID });
      setInviteLink(link);
      setMessage(buildInviteMessage(pool?.name || "", username, link, pool?.pool_depth));
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to generate invite link.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLinkLoading(false);
    }
  }

  function switchToMessage() {
    setSendMode("message");
    setMessage("");
  }

  async function handleCopyLink() {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    toast({ title: "Link copied!", status: "success", duration: 2000, isClosable: true });
  }

  async function handleSend() {
    if (emails.length === 0) {
      toast({ title: "Add at least one email.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!message.trim()) {
      toast({ title: "Message cannot be empty.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setSending(true);
    try {
      const subject = inviteSubject(sendMode, pool?.name);
      await sendWcInvites(WC_SEASON_ID, poolId, emails, message, subject, sendMode);
      setEmails([]);
      if (sendMode === "message") setMessage("");
      toast({ title: sendMode === "invite" ? "Invites sent!" : "Message sent!", status: "success", duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Failed to send.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setSending(false);
    }
  }

  const isCreator  = pool && user && pool.creator_user_id === user.id;
  const isAdmin    = isCreator || !!pool?.current_user_is_co_admin;
  const draftDone  = pool && !["pending", "drafting"].includes(pool.status);

  async function handleToggleCoAdmin(userId, newVal) {
    try {
      await toggleWcCoAdmin(WC_SEASON_ID, poolId, userId, newVal);
      setPool((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.user_id === userId ? { ...m, is_co_admin: newVal } : m
        ),
      }));
    } catch {
      toast({ title: "Failed to update co-admin status", status: "error", duration: 3000, isClosable: true });
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="WORLD CUP POOL SETUP"
        onBack={() => navigate("/world-cup")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        {loading ? (
          <Flex justify="center" align="center" minH="300px"><Spinner size="xl" /></Flex>
        ) : loadError ? (
          <Alert status="error" borderRadius="md"><AlertIcon />{loadError}</Alert>
        ) : (
          <VStack align="stretch" spacing={6}>

            {/* Pool name + status header */}
            <HStack spacing={4} align="center">
              <Heading size="lg">{pool.name}</Heading>
              {pool.is_private && (
                <Badge colorScheme="gray" fontSize="sm" px={2} py={1} borderRadius="md">
                  <HStack spacing={1}><LockIcon boxSize={3} /><Text>Private</Text></HStack>
                </Badge>
              )}
              <Badge colorScheme="blue" fontSize="sm" px={2} py={1} borderRadius="md" textTransform="capitalize">
                {pool.status}
              </Badge>
            </HStack>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">

              {/* Pool Settings */}
              <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8} h="100%">
                <VStack align="stretch" spacing={5} h="100%">
                  <Heading size="md" color={TOP_BAR_BLUE}>Pool Settings</Heading>

                  {saveError && (
                    <Alert status="error" borderRadius="md"><AlertIcon />{saveError}</Alert>
                  )}

                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Pool Name</FormLabel>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        isDisabled={!isCreator}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Buy-in ($)</FormLabel>
                      <Input
                        type="number"
                        min={1}
                        value={poolDepth}
                        onChange={(e) => setPoolDepth(e.target.value)}
                        placeholder="None"
                        isDisabled={!isCreator}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={2} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Max Users (2–12)</FormLabel>
                      <NumberInput min={2} max={12} value={maxUsers} onChange={(v) => setMaxUsers(v)} isDisabled={!isCreator || draftDone}>
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Countries Per User (1–10)</FormLabel>
                      <NumberInput min={1} max={10} value={teamsPerUser} onChange={(v) => setTeamsPerUser(v)} isDisabled={!isCreator || draftDone}>
                        <NumberInputField />
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel fontSize="sm">Draft Start Date & Time</FormLabel>
                    <HStack spacing={2}>
                      <Input
                        type="date"
                        value={draftDate}
                        onChange={(e) => setDraftDate(e.target.value)}
                        isDisabled={!isCreator || draftDone}
                        flex={2}
                      />
                      <Select
                        value={draftHour}
                        onChange={(e) => setDraftHour(Number(e.target.value))}
                        isDisabled={!isCreator || draftDone}
                        flex={1}
                      >
                        {Array.from({ length: 24 }, (_, h) => {
                          const label = h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
                          return <option key={h} value={h}>{label}</option>;
                        })}
                      </Select>
                      <Select
                        value={draftMinute}
                        onChange={(e) => setDraftMinute(Number(e.target.value))}
                        isDisabled={!isCreator || draftDone}
                        flex={1}
                      >
                        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                          <option key={m} value={m}>:{String(m).padStart(2, "0")}</option>
                        ))}
                      </Select>
                    </HStack>
                  </FormControl>

                  <Divider />

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Private Pool</FormLabel>
                      <Text fontSize="xs" color="gray.500">Invite only — visible but not joinable</Text>
                    </Box>
                    <Switch
                      isChecked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      colorScheme="blue"
                      isDisabled={!isCreator || draftDone}
                    />
                  </HStack>

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Enable Swap</FormLabel>
                      <Text fontSize="xs" color="gray.500">Allow one swap after the group stage</Text>
                    </Box>
                    <Switch
                      isChecked={swapEnabled}
                      onChange={(e) => setSwapEnabled(e.target.checked)}
                      colorScheme="blue"
                      isDisabled={!isCreator || draftDone}
                    />
                  </HStack>

                  <HStack justify="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>Weekly Recap Emails</FormLabel>
                      <Text fontSize="xs" color="gray.500">Send automated weekly updates to all members</Text>
                    </Box>
                    <Switch
                      isChecked={weeklyRecap}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setWeeklyRecap(val);
                        try {
                          await updateWcPool(WC_SEASON_ID, poolId, { weekly_recap_enabled: val });
                          toast({ title: val ? "Weekly recaps enabled." : "Weekly recaps disabled.", status: "success", duration: 2000, isClosable: true });
                        } catch {
                          setWeeklyRecap(!val);
                          toast({ title: "Failed to update recap setting.", status: "error", duration: 3000, isClosable: true });
                        }
                      }}
                      colorScheme="blue"
                      isDisabled={!isCreator}
                    />
                  </HStack>

                  {isCreator && (
                    <Flex w="100%" gap={2}>
                      <Button
                        flex="1"
                        size={{ base: "sm", md: "md" }}
                        bg={TOP_BAR_BLUE}
                        color="white"
                        _hover={{ bg: "#00004a" }}
                        isLoading={saving}
                        onClick={handleSave}
                      >
                        Save<Box as="span" display={{ base: "none", sm: "inline" }}> Changes</Box>
                      </Button>
                      <Button
                        flex="1"
                        size={{ base: "sm", md: "md" }}
                        variant="outline"
                        borderColor={TOP_BAR_BLUE}
                        color={TOP_BAR_BLUE}
                        _hover={{ bg: "gray.50" }}
                        onClick={() => navigate(`/world-cup/current/${poolId}`)}
                      >
                        Open Pool
                      </Button>
                      <Button
                        flex="1"
                        size={{ base: "sm", md: "md" }}
                        variant="outline"
                        borderColor="orange.400"
                        color="orange.500"
                        _hover={{ bg: "orange.50" }}
                        isLoading={sendingRecap}
                        onClick={async () => {
                          setSendingRecap(true);
                          try {
                            await sendWcRecapTest(WC_SEASON_ID, poolId);
                            toast({ title: "Recap queued — check your email in a moment.", status: "success", duration: 4000, isClosable: true });
                          } catch (err) {
                            toast({ title: err?.response?.data?.detail || "Failed to send test recap.", status: "error", duration: 4000, isClosable: true });
                          } finally {
                            setSendingRecap(false);
                          }
                        }}
                      >
                        <Box as="span" display={{ base: "none", sm: "inline" }}>Send </Box>Test Recap
                      </Button>
                    </Flex>
                  )}
                </VStack>
              </Box>

              {/* Payouts */}
              <Box
                bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8} h="100%"
              >
                <VStack align="stretch" spacing={5}>
                  <HStack justify="space-between" align="baseline">
                    <HStack spacing={2}>
                      <Heading size="md" color={TOP_BAR_BLUE}>Payouts</Heading>
                    </HStack>
                    {pool?.total_pot != null && (
                      <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                        Total Pot: <Text as="span" color={TOP_BAR_BLUE} fontWeight="bold">${pool.total_pot.toFixed(2)}</Text>
                      </Text>
                    )}
                  </HStack>

                  {payoutError && (
                    <Alert status="error" borderRadius="md"><AlertIcon />{payoutError}</Alert>
                  )}

                  <VStack align="stretch" spacing={2}>
                    {payouts.map((row, i) => (
                      <HStack key={i} spacing={3}>
                        <Input
                          value={row.label}
                          onChange={(e) => { const next = [...payouts]; next[i] = { ...next[i], label: e.target.value }; setPayouts(next); }}
                          placeholder="Label (e.g. 1st Place)"
                          size="sm"
                          flex="1"
                        />
                        <Input
                          value={row.amount}
                          onChange={(e) => { const next = [...payouts]; next[i] = { ...next[i], amount: e.target.value }; setPayouts(next); }}
                          placeholder="$0"
                          size="sm"
                          w="100px"
                          type="number"
                          min={0}
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Remove row"
                          onClick={() => setPayouts(payouts.filter((_, j) => j !== i))}
                        />
                      </HStack>
                    ))}
                  </VStack>

                  {payouts.length > 0 && (
                    <PayoutTotalLine
                      amounts={payouts.map((r) => r.amount)}
                      pot={pool?.total_pot}
                    />
                  )}

                  {isAdmin && (
                    <HStack>
                      <Button
                        size="sm"
                        variant="outline"
                        borderColor={TOP_BAR_BLUE}
                        color={TOP_BAR_BLUE}
                        onClick={() => setPayouts([...payouts, { label: "", amount: "" }])}
                      >
                        + Add Row
                      </Button>
                      <Button
                        size="sm"
                        bg={TOP_BAR_BLUE}
                        color="white"
                        _hover={{ bg: "#00004a" }}
                        isLoading={savingPayouts}
                        onClick={handleSavePayouts}
                      >
                        Save Payouts
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </Box>
            </SimpleGrid>

            {/* Members */}
            <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Heading size="md" color={TOP_BAR_BLUE}>Members</Heading>
                  <HStack spacing={3} flexWrap="wrap">
                    {(() => {
                      const members = pool.members || [];
                      const paidCount = members.filter((m) => m.paid).length;
                      const depth = pool.pool_depth;
                      return (
                        <>
                          <Text fontSize="sm" color="gray.500">
                            {pool.user_count}{pool.max_users ? ` / ${pool.max_users}` : ""} joined
                          </Text>
                          <Badge colorScheme={paidCount === members.length && members.length > 0 ? "green" : "orange"} fontSize="sm" px={2}>
                            {paidCount} / {members.length} paid
                          </Badge>
                          {depth && (
                            <Text fontSize="sm" color="gray.500">
                              ${paidCount * depth} / ${members.length * depth} collected
                            </Text>
                          )}
                        </>
                      );
                    })()}
                  </HStack>
                </HStack>

                {pool.members && pool.members.length > 0 ? (
                  <Box overflowX="auto" overflowY="auto" maxH="400px">
                    <Table size="sm" variant="simple">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          <Th>Member</Th>
                          <Th>Email</Th>
                          <Th textAlign="center">Paid</Th>
                          <Th textAlign="center">Co-Admin</Th>
                          {isCreator && <Th />}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {pool.members.map((m) => (
                          <Tr key={m.user_id}>
                            <Td>
                              <HStack spacing={2}>
                                <Circle size="28px" bg={TOP_BAR_BLUE} color="white" fontWeight="bold" fontSize="xs" flexShrink={0}>
                                  {(m.username || "?").charAt(0).toUpperCase()}
                                </Circle>
                                <Text fontWeight="semibold" fontSize="sm">{m.username || "—"}</Text>
                                {m.user_id === pool.creator_user_id && (
                                  <Badge colorScheme="blue" fontSize="xs">Admin</Badge>
                                )}
                              </HStack>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600" noOfLines={1}>{m.email || "—"}</Text>
                            </Td>
                            <Td textAlign="center">
                              <Checkbox
                                isChecked={!!m.paid}
                                isDisabled={!isAdmin}
                                colorScheme="green"
                                onChange={(e) => handleTogglePaid(m.user_id, e.target.checked)}
                              />
                            </Td>
                            <Td textAlign="center">
                              {m.user_id !== pool.creator_user_id ? (
                                isCreator ? (
                                  <Checkbox
                                    isChecked={!!m.is_co_admin}
                                    colorScheme="blue"
                                    onChange={(e) => handleToggleCoAdmin(m.user_id, e.target.checked)}
                                  />
                                ) : m.is_co_admin ? (
                                  <Badge colorScheme="blue" fontSize="2xs">Co-Admin</Badge>
                                ) : null
                              ) : null}
                            </Td>
                            {isCreator && (
                              <Td textAlign="right" px={1}>
                                {m.user_id !== user.id && (
                                  <Tooltip label="Remove member" hasArrow>
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="red"
                                      aria-label="Remove member"
                                      onClick={() => handleRemoveMember(m.user_id)}
                                    />
                                  </Tooltip>
                                )}
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="gray.500">No members yet.</Text>
                )}
              </VStack>
            </Box>

            {/* Invite/Message Players */}
            {isAdmin && (
              <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={8}>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between" align="center">
                    <Heading size="md" color={TOP_BAR_BLUE}>Invite/Message Players</Heading>
                    <Button
                      size="xs"
                      minH="44px"
                      variant="outline"
                      borderColor={TOP_BAR_BLUE}
                      color={TOP_BAR_BLUE}
                      _hover={{ bg: "gray.50" }}
                      onClick={addAllMembers}
                    >
                      Add all members
                    </Button>
                  </HStack>

                  {/* Mode toggle */}
                  <HStack spacing={0} borderRadius="md" overflow="hidden" border={`1.5px solid ${invitesClosed ? "#b0b0b0" : TOP_BAR_BLUE}`} alignSelf="flex-start">
                    <Button
                      size="sm"
                      minH="44px"
                      borderRadius="0"
                      bg={sendMode === "invite" ? TOP_BAR_BLUE : "white"}
                      color={sendMode === "invite" ? "white" : invitesClosed ? "gray.400" : TOP_BAR_BLUE}
                      _hover={{ bg: invitesClosed ? "white" : sendMode === "invite" ? TOP_BAR_BLUE : "gray.50" }}
                      onClick={invitesClosed ? undefined : switchToInvite}
                      isLoading={sendMode === "invite" && linkLoading}
                      isDisabled={invitesClosed}
                      cursor={invitesClosed ? "not-allowed" : "pointer"}
                      title={invitesClosed ? "Invites are closed — draft has started" : undefined}
                      px={5}
                    >
                      Invite
                    </Button>
                    <Button
                      size="sm"
                      minH="44px"
                      borderRadius="0"
                      bg={sendMode === "message" ? TOP_BAR_BLUE : "white"}
                      color={sendMode === "message" ? "white" : TOP_BAR_BLUE}
                      _hover={{ bg: sendMode === "message" ? TOP_BAR_BLUE : "gray.50" }}
                      onClick={switchToMessage}
                      px={5}
                    >
                      Message
                    </Button>
                  </HStack>

                  {/* Invite link display */}
                  {sendMode === "invite" && inviteLink && (
                    <HStack bg="gray.50" px={3} py={2} borderRadius="md" border="1px solid #e2e8f0">
                      <Text fontSize="xs" color="gray.600" flex="1" isTruncated>{inviteLink}</Text>
                      <Button size="xs" minH="44px" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={handleCopyLink} flexShrink={0}>
                        Copy Link
                      </Button>
                    </HStack>
                  )}

                  <FormControl>
                    <FormLabel fontSize="sm">Email Addresses</FormLabel>
                    <HStack>
                      <Input
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={handleEmailKeyDown}
                        placeholder="Enter email and press Enter"
                        size="sm"
                      />
                      <Button size="sm" minH="44px" onClick={addEmail} bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }} flexShrink={0}>
                        Add
                      </Button>
                    </HStack>
                    {emails.length > 0 && (
                      <Flex mt={2} flexWrap="wrap" gap={2}>
                        {emails.map((addr) => (
                          <Tag key={addr} size="sm" borderRadius="full" variant="solid" colorScheme="blue">
                            <TagLabel>{addr}</TagLabel>
                            <TagCloseButton onClick={() => removeEmail(addr)} />
                          </Tag>
                        ))}
                      </Flex>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Message</FormLabel>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={sendMode === "message" ? "Add message here..." : ""}
                      rows={7}
                      fontSize="sm"
                    />
                  </FormControl>

                  <Button
                    bg={TOP_BAR_BLUE}
                    color="white"
                    _hover={{ bg: "#00004a" }}
                    isLoading={sending}
                    onClick={handleSend}
                    alignSelf="flex-start"
                  >
                    {sendMode === "invite"
                      ? `Send Invite${emails.length > 1 ? "s" : ""}`
                      : `Send Message${emails.length > 1 ? "s" : ""}`}
                  </Button>
                </VStack>
              </Box>
            )}

          </VStack>
        )}
      </Container>
    </Box>
  );
}
